import * as React from 'react';

import './App.css';

import { sendJIEvent } from './lib/KwizzAdJI';
import { getCurrentQueryParams } from './lib/queryParams';

import IReward from './lib/types/Reward';
import IRewardCategory from './lib/types/RewardCategory';
import IRewardPurchase from './lib/types/RewardPurchase';

import AddressEditor from './components/AddressEditor/AddressEditor';
import ConfirmPurchase from './components/ConfirmPurchase/ConfirmPurchase';
import ErrorScreen from './components/ErrorScreen/ErrorScreen';
import LoadingScreen from './components/LoadingScreen';
import RewardCategoryList from './components/RewardCategoryList/RewardCategoryList';
import SingleRewardCategoryView from
  './components/SingleRewardCategoryView/SingleRewardCategoryView';
import ThankYou from './components/ThankYou/ThankYou';
import RewardPurchaseList from './components/RewardPurchaseList/RewardPurchaseList';

import 'react-activity/dist/react-activity.css';
import {
  setFavoriteRewardId,
  getRewardContent,
  getFavoriteReward,
  createRewardPurchase,
} from './lib/RewardShopAPI';
import BackendAPI from './lib/BackendAPI';

const { deviceUDID, idToken, env, rewardCategoryId } = getCurrentQueryParams();
/**
 * Registers all component classes for all allowed app modes
 */

const views: { [name: string]: any } = {
  ThankYou,
  AddressEditor,
  ConfirmPurchase,
  ErrorScreen,
  LoadingScreen,
  RewardCategoryList,
  RewardCategoryView: SingleRewardCategoryView,
  PurchaseHistory: RewardPurchaseList,
};

type AppMode = string & keyof typeof views;

const initialState = {
  appMode: 'RewardCategoryList' as AppMode,
  userCurrentPoints: 0,
  credits: 0,
  vipCredits: 0,
  oldCredits: 0,
  errorMessage: null as string | null,
  rewardCategories: [] as IRewardCategory[],
  rewardPurchases: [] as IRewardPurchase[],
  rewardCategoryId: null as string | null,
  rewardId: null as string | null,
  rewards: [] as IReward[],
  isSettingFavoriteRewardId: false,
  favoriteRewardId: null as string | null,
  storeConfirmationMessage: null as string | null,
  storeConfirmationTitle: null as string | null,
  isAuthenticated: null as boolean | null,
  isVipUser: null as boolean | null,
  canRedeemToday: null as boolean |null,
};

type State = typeof initialState;

/**
 * This component cares for the overall app main states.
 */

class App extends React.Component<{}, State> {
  public readonly state: State = initialState;

  constructor(props: {}) {
    super(props);
    this.initialRequests();
  }

  private async initialRequests() {
    // fetch statistics and authenticate user
    await this.fetchAvailableAmount();
    await this.fetchVipPoints();
    await this.fetchOldPoints();
    await this.fetchCanRedeemToday();
    // fetch other data with idToken if authenticated, otherwise use udid
    await this.fetchRewardContent();
    await this.fetchFavoriteReward();
    this.initialRewardCategorySetup();
  }

  private initialRewardCategorySetup() {
    if (rewardCategoryId && this.state.rewardCategories.find(rc => rc.id === rewardCategoryId)) {
      this.selectRewardCategory(rewardCategoryId);
    }
    const credits = this.state.userCurrentPoints - this.state.oldCredits;
    this.setState({ credits });
    console.log(`numbers: T ${this.state.vipCredits} current: ${this.state.userCurrentPoints}`
        + ` old: ${this.state.oldCredits} shop: ${this.state.credits}`);
  }

  public render() {
    const CurrentComponent = views[this.state.appMode];
    return <CurrentComponent
      {...this.state}
      udid={getCurrentQueryParams().udid}
      onGoBack={this.goBack}
      onLogin={this.showLogin}
      onConfirmPurchase={this.confirmPurchase}
      onShowAddressEditor={this.showAddressEditor}
      onShowConfirmationScreen={this.showConfirmationScreen}
      onSelectRewardCategory={this.selectRewardCategory}
      onSelectReward={this.selectReward}
      onShowPurchaseHistory={this.showPurchaseHistory}
      onShowRewardCategoryList={this.showRewardCategoryList}
      onCloseApp={this.closeApp}
      setRewardAsFavorite={this.setRewardAsFavorite}
      appMode={this.state.appMode}
    />;
  }

  private selectReward = (rewardId: string) => {
    this.setState({ rewardId });
  }

  private selectRewardCategory = (rewardCategoryId: string) => {
    const categoryRewards = this.state.rewards
      .filter(r => r.isActive && r.rewardCategoryId === rewardCategoryId)
      .sort((a, b) => a.amount - b.amount);
    const firstReward = categoryRewards[0];
    const rewardId = firstReward ? firstReward.id : null;
    this.setState({
      rewardCategoryId,
      rewardId,
      appMode: 'RewardCategoryView',
    });
  }

  private setRewardAsFavorite = () => {
    const rewardId = this.state.rewardId;
    if (!rewardId) {
      return;
    }
    this.setState({
      isSettingFavoriteRewardId: true,
    });
    setFavoriteRewardId({ env, deviceUDID, idToken, rewardId }).then(() => {
      this.setState({
        isSettingFavoriteRewardId: false,
        favoriteRewardId: rewardId,
      });
    });
  }

  private showConfirmationScreen = () => {
    this.setState({
      appMode: 'ConfirmPurchase',
    });
  }

  private showPurchaseHistory = () => {
    this.setState({
      appMode: 'PurchaseHistory',
    });
  }

  private showRewardCategoryList = () => {
    this.setState({
      appMode: 'RewardCategoryList',
    });
  }

  private showAddressEditor = () => {
    this.setState({
      appMode: 'AddressEditor',
    });
  }

  private showLogin = () => {
    sendJIEvent('login');
  }

  private confirmPurchase = () => {
    const rewards = this.state.rewards;
    const rewardId = this.state.rewardId;
    if (!rewardId) {
      throw new Error("Can't purchase before selecting a reward to purchase");
    }
    BackendAPI.createRewardPurchaseNotShop({ env, rewardId, rewards, deviceUDID });
    createRewardPurchase({ env, rewardId, deviceUDID, idToken })
      .then(({ storeConfirmationMessage, storeConfirmationTitle }) => {
        this.setState({ storeConfirmationMessage, storeConfirmationTitle, appMode: 'ThankYou' });
      })
      .catch((error) => {
        console.log(error);
        this.setState({ errorMessage: String(error), appMode: 'ErrorScreen' });
      });
  }

  private goBack = () => {
    this.setState({
      appMode: {
        AddressEditor: 'RewardCategoryView',
        ConfirmPurchase: 'RewardCategoryView',
        RewardCategoryView: 'RewardCategoryList',
        PurchaseHistory: 'RewardCategoryList',
      }[this.state.appMode],
    });
  }

  private closeApp() {
    sendJIEvent('finished');
  }

  private async fetchAvailableAmount() {
    return BackendAPI.fetchStatistics(idToken)
      .then((statistics) => {
        this.setState({ userCurrentPoints: statistics.currentPoints, isAuthenticated:true });
      })
      .catch((error) => {
        console.log('Could not fetch available points:', error);
      });
  }

  private async fetchVipPoints() {
    return BackendAPI.fetchVipPoints(idToken)
        .then((vip) => {
          const vipCredits = Number.parseInt(vip.value, 0);
          const isVipUser = vipCredits > 0;
          this.setState({ isVipUser, vipCredits });
        })
        .catch((error) => {
          console.log('Could not fetch vip points:', error);
        });
  }

  private async fetchOldPoints() {
    return BackendAPI.fetchOldPoints(idToken)
        .then((vip) => {
          const oldCredits = Number.parseInt(vip.value, 0);
          this.setState({ oldCredits });
        })
        .catch((error) => {
          console.log('Could not fetch old points:', error);
        });
  }

  private async fetchCanRedeemToday() {
    return BackendAPI.fetchCanRedeemToday(idToken)
      .then(() => {
        this.setState({ canRedeemToday: false });
      })
      .catch((error) => {
        console.log('Could not fetch old points:', error);
        this.setState({ canRedeemToday: true });
      });
  }

  private getAuthParams(): {idToken?: string, deviceUDID?: string, env: string} {
    return this.state.isAuthenticated
        ? { idToken, env }
        : { deviceUDID, env };
  }

  private async fetchRewardContent() {
    try {
      // do not filter rewards and categories here
      // inactive rewards and categories may be used in the users purchase overview
      const { rewards, rewardPurchases, rewardCategories } =
      await getRewardContent({ ...this.getAuthParams(), limit: 50 });
      return this.setState({ rewards, rewardCategories, rewardPurchases });
    } catch (error) {
      return this.setState({ errorMessage: String(error), appMode: 'ErrorScreen' });
    }
  }

  private async fetchFavoriteReward() {
    try {
      const favoriteReward = await getFavoriteReward(this.getAuthParams());
      return this.setState({ favoriteRewardId: favoriteReward.id });
    } catch (error) {
      return this.setState({ errorMessage: String(error), appMode: 'ErrorScreen' });
    }
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx